import { decode as decodeBase64 } from 'base-64';

import { toConversationError } from './errors';

import { fetchClient } from '@/util/fetchClient';

const TOKEN_PREFIX = 'prt.';

/**
 * A short-lived token for the effective user's inbox channel. It names the
 * acted-as account while acting as someone, because the request carries the
 * act-as header like any other.
 */
export async function getRealtimeToken(): Promise<string> {
  try {
    const { data } = await fetchClient.GET('/v2/realtime/token');

    if (!data) {
      throw new TypeError('Empty realtime token response');
    }

    return data.data.token;
  } catch (error) {
    throw toConversationError(error);
  }
}

/**
 * The user id a realtime token is for, or null when the token is not one this
 * client understands.
 *
 * The token is a signed JWT behind a `prt.` prefix, which keeps the realtime
 * service from mistaking it for an identity-provider token. The client reads
 * the claim only to name the channel; the server verifies the signature, and
 * a channel taken from anywhere else could be refused by it.
 */
export function realtimeTokenUserId(token: string): string | null {
  if (!token.startsWith(TOKEN_PREFIX)) {
    return null;
  }

  const payload = token.slice(TOKEN_PREFIX.length).split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    const claims: unknown = JSON.parse(decodeBase64(padded));

    const sub =
      typeof claims === 'object' && claims !== null ? (claims as { sub?: unknown }).sub : null;

    return typeof sub === 'string' && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}
